import React from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PricingJobs = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // GoJobs pricing = for Employers/Companies only
  const plans = [
    {
      name: 'Basic',
      price: 0,
      badge: 'Free',
      badgeColor: 'bg-gray-500',
      features: [
        '1 active job listing',
        'Standard visibility',
        'Basic applicant inbox',
        'Edit listing anytime',
        'Listing expires in 30 days',
      ],
    },
    {
      name: 'Premium',
      price: 19,
      badge: 'Popular',
      badgeColor: 'bg-blue-500',
      features: [
        '5 active job listings',
        'Priority visibility in browse',
        'Company profile highlight',
        'Applicant management tools',
        'Basic analytics (views & clicks)',
        'Email notifications',
      ],
    },
    {
      name: 'Pro',
      price: 39,
      badge: 'Best Value',
      badgeColor: 'bg-yellow-500',
      features: [
        'Unlimited active job listings',
        'Featured placements (sponsored slots)',
        'Advanced analytics (source & conversion)',
        'Team access (3 seats)',
        'Priority support',
        'Verification priority (ABN badge fast-track)',
      ],
    },
  ];

  const startCheckout = async (planCode) => {
    try {
      const origin = window.location.origin;

      // If your backend uses a different endpoint for GoJobs, change it here.
      // Example: `${BACKEND_URL}/api/gojobs/subscriptions/checkout`
      const response = await axios.post(`${BACKEND_URL}/api/subscriptions/checkout`, null, {
        params: {
          plan_code: planCode,   // e.g. 'gojobs_premium'
          origin_url: origin,
        },
      });

      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Checkout failed:', error);
      toast.error('Failed to start checkout');
    }
  };

  const handleSubscribe = async (plan) => {
    // Guests: must register/login as employer
    if (!user) {
      toast.info('Please register or log in as a company to subscribe.');
      navigate('/jobs/register');
      return;
    }

    // Only employers/companies can subscribe
    if (user.role !== 'employer') {
      toast.error('Company accounts only: GoJobs plans are for employers.');
      return;
    }

    // Free plan
    if (plan.price === 0) {
      toast.info('You are on the Basic plan.');
      return;
    }

    const planCode = `gojobs_${plan.name.toLowerCase()}`; // gojobs_basic / gojobs_premium / gojobs_pro
    await startCheckout(planCode);
  };

  const getButtonState = (plan) => {
    const isLoggedIn = !!user;
    const isEmployer = user?.role === 'employer';

    if (plan.price === 0) {
      return {
        label: isEmployer ? 'Current Plan' : 'Free Plan',
        disabled: isEmployer, // treat as current for employer; you can later tie to real subscription status
      };
    }

    if (!isLoggedIn) return { label: 'Register as Company', disabled: false };
    if (!isEmployer) return { label: 'Company Only', disabled: true };

    return { label: `Subscribe to ${plan.name}`, disabled: false };
  };

  return (
    <div className="container mx-auto px-4 py-12" data-testid="gojobs-pricing-page">
      <div className="text-center mb-12 space-y-4">
        <h1 className="text-4xl font-bold">GoJobs Pricing</h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Plans for employers to post roles, manage applicants, and hire with confidence. Candidates can browse jobs for free.
        </p>
        <div className="inline-block bg-accent-yellow/20 border border-accent-yellow px-4 py-2 rounded-full text-sm font-medium">
          🎉 30-day billing cycle • Cancel anytime
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
        {plans.map((plan) => {
          const btn = getButtonState(plan);

          return (
            <Card
              key={plan.name}
              className={`relative ${plan.name === 'Premium' ? 'border-primary border-2 shadow-lg' : ''}`}
              data-testid={`gojobs-plan-${plan.name.toLowerCase()}`}
            >
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className={`${plan.badgeColor} text-white`}>{plan.badge}</Badge>
                </div>
              )}

              <CardHeader className="text-center pt-8">
                <CardTitle className="text-2xl">{plan.name}</CardTitle>
                <div className="mt-4">
                  <span className="text-4xl font-bold">${plan.price}</span>
                  <span className="text-muted-foreground">/30 days</span>
                </div>
              </CardHeader>

              <CardContent className="space-y-6">
                <ul className="space-y-3">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <svg
                        className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSubscribe(plan)}
                  className={`w-full rounded-xl ${
                    plan.price === 0
                      ? 'bg-muted hover:bg-muted/80 text-foreground'
                      : plan.name === 'Premium'
                      ? 'bg-primary hover:bg-primary-hover'
                      : 'bg-accent-yellow text-accent-yellow-foreground hover:opacity-90'
                  }`}
                  disabled={btn.disabled}
                  data-testid={`gojobs-subscribe-${plan.name.toLowerCase()}-button`}
                >
                  {btn.label}
                </Button>

                {plan.price > 0 && user && user.role !== 'employer' && (
                  <p className="text-xs text-muted-foreground text-center">
                    GoJobs subscriptions are only for employers who post and manage job ads.
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default PricingJobs;
